import {
  createRunnerInitialState, type RunnerDisplayState, type RunnerEnvironmentKind, type RunnerFrame, type RunnerCalibNormMap,
} from '../domain/RunnerDisplayState.ts';
import type { KeypointType } from 'shared/lib/tv-game/pose.ts';
import type { NativeGameFrame } from 'shared/lib/tv-game/nativeFrame.ts';
import { RunnerCanvasLayer } from '../infrastructure/canvas/runner/RunnerCanvasLayer.ts';
import {
  createRunner3DScene, type Runner3DAction, type Runner3DScene,
} from '../games/runner-3d/runner3dScene.ts';
import type { TvQualityProfile } from '../infrastructure/qualityProfile.ts';
import { getRunnerStage } from '../games/runner/stages/runnerStages.ts';
import { patchRunnerUi, resetRunnerUi } from '../games/runner/state/runnerUiStore.ts';
import { setCalibrationAlert } from '../games/shared/state/calibrationAlertStore.ts';
import { parseCalibrationHint } from 'shared/lib/tv-game/nativeFrame.ts';

export class RunnerDisplayEngine {
  private readonly state: RunnerDisplayState = createRunnerInitialState();
  private canvasLayer: RunnerCanvasLayer | null = null;

  /**
   * Режим «3D» на телефоне: та же сцена, что в приложениях (улица, персонаж, препятствия),
   * в своей WebGL-канве под общей 2D `#canvas`. 2D-слой в этом режиме рисует только калибровку.
   */
  private environment: Runner3DScene | null = null;

  private environmentHost: HTMLDivElement | null = null;

  onCalibrationComplete?: () => void;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly quality: TvQualityProfile,
  ) {}

  /** `environment` — режим, выбранный на телефоне вместе с трассой (по умолчанию — фоновое видео). */
  setStage(stageId: string, environment: RunnerEnvironmentKind = 'video'): void {
    this.state.stageId = stageId;
    this.state.environment = environment;
    patchRunnerUi({ selectedStageId: stageId });
    this.rebuildCanvasLayer();
  }

  private rebuildCanvasLayer(): void {
    this.canvasLayer?.destroy();
    this.destroyEnvironment();
    const stage = getRunnerStage(this.state.stageId);
    this.canvasLayer = new RunnerCanvasLayer(this.canvas, stage.assets, this.state.environment !== '3d');
    this.ensureEnvironment();
    if (this.state.phase === 'playing') {
      this.resumeBackground();
    }
  }

  /**
   * Создаёт 3D-сцену, если выбран режим «3D». Сцена грузится сразу и стоит на первом
   * кадре, скрытая, пока идёт лобби/калибровка. Нет WebGL или сцена не загрузилась —
   * возвращаемся к фоновому видео.
   */
  private ensureEnvironment(): void {
    if (this.state.environment !== '3d' || this.environment) return;
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;inset:0;z-index:0;visibility:hidden;';
    // Перед `#canvas` с тем же z-index: 2D-слой (калибровка) рисуется поверх.
    this.canvas.parentElement?.insertBefore(host, this.canvas);
    try {
      // Слабым ТВ — рендер в родном размере без сглаживания (как у «Полёта птицы»).
      const isLight = this.quality !== 'high';
      this.environment = createRunner3DScene({
        host,
        assetsBase: '/media/runner-3d/',
        world: this.state.stageId === 'datacenter' ? 'datacenter' : 'street',
        pixelRatio: isLight ? 1 : 1.5,
        antialias: !isLight,
        onError: (message) => {
          console.error('[runner-3d]', message);
          this.fallBackToVideo();
        },
      });
      this.environmentHost = host;
    } catch (err) {
      host.remove();
      console.error('[runner-env] WebGL is not available', err);
      this.fallBackToVideo();
    }
  }

  private fallBackToVideo(): void {
    if (this.state.environment !== '3d') return;
    this.state.environment = 'video';
    this.rebuildCanvasLayer();
    if (this.state.phase === 'playing') this.canvasLayer?.startVideo();
  }

  private destroyEnvironment(): void {
    this.environment?.destroy();
    this.environment = null;
    this.environmentHost?.remove();
    this.environmentHost = null;
  }

  /** Улицу видно только во время забега; в лобби и на калибровке она скрыта. */
  private syncEnvironmentVisibility(): void {
    if (!this.environmentHost) return;
    const visible = this.state.phase === 'playing' || this.state.phase === 'paused' || this.state.phase === 'gameover';
    const value = visible ? 'visible' : 'hidden';
    if (this.environmentHost.style.visibility !== value) this.environmentHost.style.visibility = value;
  }

  // Фон за дорогой — видео или 3D-улица; активен ровно один из двух.
  private startBackground(): void {
    this.canvasLayer?.startVideo();
    this.environment?.start();
  }

  private resumeBackground(): void {
    this.canvasLayer?.resumeVideo();
    this.environment?.resume();
  }

  private pauseBackground(): void {
    this.canvasLayer?.pauseVideo();
    this.environment?.pause();
  }

  private stopBackground(): void {
    this.canvasLayer?.stopVideo();
    this.environment?.restart();
  }

  getPhase(): RunnerDisplayState['phase'] {
    return this.state.phase;
  }

  setPhase(phase: RunnerDisplayState['phase']): void {
    this.state.phase = phase;
    if (phase === 'playing') {
      this.startBackground();
    } else if (phase === 'lobby') {
      this.stopBackground();
    }
  }

  startCalibration(): void {
    this.state.phase = 'calibration';
    this.state.frame = null;
    this.state.calibKeypoints = new Map();
    this.state.detectionProgress = null;
    if (!this.canvasLayer) {
      this.rebuildCanvasLayer();
    }
    this.ensureEnvironment(); // после reset() улицы нет, а трасса та же
    this.environment?.restart();
    patchRunnerUi({
      hudVisible: false,
      pauseVisible: false,
      gameOverVisible: false,
      stageSelectVisible: false,
    });
  }

  startGame(): void {
    this.state.frame = null;
    this.state.phase = 'playing';
    if (!this.canvasLayer) {
      this.rebuildCanvasLayer();
    } else {
      this.ensureEnvironment();
    }
    this.startBackground();
    patchRunnerUi({
      hudVisible: true,
      pauseVisible: false,
      gameOverVisible: false,
      stageSelectVisible: false,
      score: 0,
      lives: 3,
    });
    setCalibrationAlert(null);
  }

  private buildCalibKeypointsFromNativeFrame(frame: NativeGameFrame): RunnerCalibNormMap {
    const map: RunnerCalibNormMap = new Map();
    const kps = frame.player1?.keypoints ?? [];
    for (const kp of kps) {
      map.set(kp.type, { nx: kp.x, ny: kp.y });
    }
    return map;
  }

  onCalibFrame(frame: NativeGameFrame): void {
    if (this.state.phase !== 'calibration') {
      this.startCalibration();
    }
    this.state.calibKeypoints = this.buildCalibKeypointsFromNativeFrame(frame);
    if (frame.detectionProgress !== undefined) {
      this.state.detectionProgress = frame.detectionProgress;
    }
    if ('calibration' in frame) {
      setCalibrationAlert(frame.calibration ?? null);
    }
  }

  onGameFrame(rawFrame: unknown): void {
    if (!rawFrame || typeof rawFrame !== 'object') return;
    const raw = rawFrame as Record<string, unknown>;

    // Socket.IO path: raw JSON with gameState PREPARING (KeypointsDataMultiplayer)
    if (raw['gameState'] === 'PREPARING') {
      if (this.state.phase !== 'calibration') {
        this.startCalibration();
      }
      const p1 = raw['player1'] as { keypoints?: Array<{ type: string; x: number; y: number }> } | null;
      if (p1?.keypoints) {
        const map: RunnerCalibNormMap = new Map();
        for (const kp of p1.keypoints) {
          if (kp.type && typeof kp.x === 'number' && typeof kp.y === 'number') {
            map.set(kp.type as KeypointType, { nx: kp.x, ny: kp.y });
          }
        }
        this.state.calibKeypoints = map;
      }
      const dp = raw['detectionProgress'];
      if (typeof dp === 'number') this.state.detectionProgress = dp;
      if ('calibration' in raw) {
        const cal = parseCalibrationHint(raw['calibration']);
        if (cal !== undefined) setCalibrationAlert(cal);
      }
      return;
    }

    const frame = rawFrame as RunnerFrame;

    this.state.frame = frame;
    this.syncScene(frame);

    // Any real gameplay frame means we've left the preparation screen.
    setCalibrationAlert(null);

    const prev = this.state.phase;

    if (frame.gameState === 'STARTED') {
      if (prev === 'calibration') {
        this.onCalibrationComplete?.();
      }
      if (prev !== 'playing') {
        this.state.phase = 'playing';
        // Resume from where it was paused; restart from the beginning otherwise.
        if (prev === 'paused') {
          this.resumeBackground();
        } else {
          this.startBackground();
        }
      }
      patchRunnerUi({
        hudVisible: true,
        pauseVisible: false,
        gameOverVisible: false,
        score: frame.player.score,
        lives: frame.player.lives,
      });
    } else if (frame.gameState === 'PAUSED') {
      this.state.phase = 'paused';
      this.pauseBackground();
      patchRunnerUi({
        hudVisible: false,
        pauseVisible: true,
        gameOverVisible: false,
        score: frame.player.score,
        lives: frame.player.lives,
      });
    } else if (frame.gameState === 'GAME_OVER') {
      this.state.phase = 'gameover';
      this.stopBackground();
      patchRunnerUi({
        hudVisible: false,
        pauseVisible: false,
        gameOverVisible: true,
        score: frame.player.score,
        lives: frame.player.lives,
      });
    }
  }

  /** Кадр движка → состояние 3D-сцены. Сцена считает игрока на 880 — z сдвигаем от `playerZ`. */
  private syncScene(frame: RunnerFrame): void {
    if (!this.environment) return;
    const shift = 880 - frame.player.playerZ;
    this.environment.setState({
      running: frame.gameState === 'STARTED',
      player: {
        lane: frame.player.lane,
        action: frame.player.animationType as Runner3DAction,
        progress: frame.player.animationProgress,
        lives: frame.player.lives,
      },
      obstacles: frame.obstacles.map((o) => ({
        id: o.id, type: o.type, lane: o.lane, z: o.zPos + shift, destroyed: o.isDestroyed,
      })),
    });
  }

  tick(): void {
    if (!this.canvasLayer) return;
    this.syncEnvironmentVisibility();
    this.canvasLayer.render(this.state);
  }

  reset(): void {
    this.state.phase = 'lobby';
    this.state.frame = null;
    this.canvasLayer?.stopVideo();
    // Цикл three.js не должен крутиться, пока идёт другая игра; трасса и режим
    // остаются — улицу пересоздадут калибровка или старт.
    this.destroyEnvironment();
    resetRunnerUi();
    setCalibrationAlert(null);
  }

  destroy(): void {
    this.canvasLayer?.destroy();
    this.canvasLayer = null;
    this.destroyEnvironment();
  }
}
